/**
 * Rewrites an absolute storage URL (e.g. a presigned object-storage URL
 * returned by the auth-api-gateway) into a same-origin path served by the BFF route at
 * `/api/files/picture/...`. Mobile clients still consume the original URL
 * verbatim — this rewrite is purely a browser concern (RFC1918 hosts,
 * mixed-content, future https-only constraints).
 *
 * `blob:` and `data:` inputs pass through unchanged so local preview
 * ObjectURLs from `URL.createObjectURL` continue to work. Anything else
 * (bare file UUID, opaque token, relative path) returns `undefined` so the
 * `<Avatar>` falls back to its `children` (initials) instead of firing a
 * spurious request to a relative path that 404s at the Next.js root.
 */
export function toBffPictureProxyUrl(input: string | null | undefined): string | undefined {
  if (!input) return undefined;
  if (input.startsWith('blob:') || input.startsWith('data:')) return input;
  if (!/^https?:\/\//i.test(input)) return undefined;
  try {
    const url = new URL(input);
    const pathname = url.pathname.startsWith('/') ? url.pathname.slice(1) : url.pathname;
    // A root-only pathname (e.g. `http://host/` or `http://host/?q=1`)
    // collapses to an empty string here. Forwarding `/api/files/picture/`
    // would 404 at the BFF and trigger a spurious request, so fall back to
    // `undefined` and let callers render the initials placeholder.
    if (!pathname) return undefined;
    return `/api/files/picture/${pathname}${url.search}`;
  } catch {
    return undefined;
  }
}
